import { defaultSettings, emptyData } from './defaults';
import { emptyDeal, type CostLine, type Deal } from './flip';
import { STATUSES, type AppData, type Car, type Settings, type Status } from './types';

export const STORAGE_KEY = 'car-flip-planner:v1';

type Json = Record<string, unknown>;
const isObj = (v: unknown): v is Json => typeof v === 'object' && v !== null && !Array.isArray(v);
const num = (v: unknown, fallback: number): number =>
  typeof v === 'number' && Number.isFinite(v) ? v : fallback;
const str = (v: unknown, fallback = ''): string => (typeof v === 'string' ? v : fallback);

const DEAL_NUMBERS = [
  'purchasePrice', 'buyerFeePct', 'buyerFeeFixed', 'collection', 'contingencyPct', 'mot', 'valet',
  'historyCheck', 'tax', 'insurancePerDay', 'storagePerDay', 'adFee', 'salePrice', 'saleFeePct',
  'holdingDays', 'capitalCostPctPerYear',
] as const;

/** A deal from untrusted data: unknown or bad fields fall back to the blank deal. */
export function cleanDeal(v: unknown): Deal {
  const base = emptyDeal();
  if (!isObj(v)) return base;
  const out: Deal = { ...base };
  for (const k of DEAL_NUMBERS) out[k] = num(v[k], base[k]);
  const lines = Array.isArray(v.repairs) ? v.repairs : [];
  out.repairs = lines.filter(isObj).map((r, i): CostLine => ({
    id: str(r.id, `line-${i}`), label: str(r.label), amount: num(r.amount, 0),
  }));
  return out;
}

function cleanSettings(v: unknown): Settings {
  const base = defaultSettings();
  if (!isObj(v)) return base;
  const t = isObj(v.targets) ? v.targets : {};
  const sc = isObj(v.scenarios) ? v.scenarios : {};
  const part = (x: unknown, b: Settings['scenarios']['cautious']) => {
    const o = isObj(x) ? x : {};
    return { salePricePct: num(o.salePricePct, b.salePricePct), repairsPct: num(o.repairsPct, b.repairsPct), extraDays: num(o.extraDays, b.extraDays) };
  };
  return {
    targets: {
      minProfit: num(t.minProfit, base.targets.minProfit),
      minRoiPct: num(t.minRoiPct, base.targets.minRoiPct),
      minProfitPerDay: num(t.minProfitPerDay, base.targets.minProfitPerDay),
    },
    scenarios: { cautious: part(sc.cautious, base.scenarios.cautious), good: part(sc.good, base.scenarios.good) },
    startingCash: Math.max(0, num(v.startingCash, base.startingCash)),
    defaults: cleanDeal(v.defaults),
  };
}

function cleanCar(v: unknown): Car | null {
  if (!isObj(v) || typeof v.id !== 'string' || !v.id) return null;
  const status = STATUSES.includes(v.status as Status) ? (v.status as Status) : 'watching';
  const checks: Record<string, boolean> = {};
  if (isObj(v.checks)) for (const [k, b] of Object.entries(v.checks)) if (b === true) checks[k] = true;
  const deal = cleanDeal(v.deal);
  const now = new Date().toISOString();
  return {
    id: v.id, title: str(v.title, 'Untitled car'), reg: str(v.reg), source: str(v.source), status,
    planned: v.planned === undefined ? deal : cleanDeal(v.planned), deal,
    boughtOn: str(v.boughtOn), soldOn: str(v.soldOn), notes: str(v.notes), checks,
    createdAt: str(v.createdAt, now), updatedAt: str(v.updatedAt, now),
  };
}

/** Turn anything into valid app data, or null if it is not app data at all. */
export function cleanData(v: unknown): AppData | null {
  if (!isObj(v) || v.version !== 1 || !Array.isArray(v.cars)) return null;
  const cars = v.cars.map(cleanCar).filter((c): c is Car => c !== null);
  return { version: 1, settings: cleanSettings(v.settings), cars };
}

export type ImportResult = { ok: true; data: AppData } | { ok: false; error: string };

/** Read an exported file. */
export function parseImport(text: string): ImportResult {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return { ok: false, error: 'That file is not valid JSON.' };
  }
  const data = cleanData(raw);
  return data ? { ok: true, data } : { ok: false, error: 'That file is not a Car Flip Planner export.' };
}

export const exportJson = (data: AppData): string => JSON.stringify(data, null, 2);

/** Load saved data, or a fresh start. Never throws: private browsing can block storage. */
export function load(): AppData {
  try {
    const text = localStorage.getItem(STORAGE_KEY);
    if (!text) return emptyData();
    return cleanData(JSON.parse(text)) ?? emptyData();
  } catch {
    return emptyData();
  }
}

/** Save, returning false if the browser refused. */
export function save(data: AppData): boolean {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    return true;
  } catch {
    return false;
  }
}
